import _, { random } from 'lodash';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AppConstant } from '../../../../../Assests/AppConstant';
import { createAWSUserPayload, formDropDownData, getActiveCountryCode, shouldHavePermission, showAlert, updateUserPayload, validateCreateUserForm } from '../../../../../Assests/Utility';
import CustomButton from '../../../../Common/CustomButton/CustomButton';
import Popup from '../../../../Common/Popup/Popup';
import UserTable from '../Children/UserTable';
import { useDispatch } from 'react-redux';
import { sagaActions } from '../../../../../../sagas/sagaActions';
import { getUserSelector } from '../../../../../../redux/reducers/reducerSlices/UserReducer';
import { useSelector } from 'react-redux';
import { CustomDynamicForm } from '../../../../Common/CustomForm/CustomDynamicForm';
import WarehouseSearch from '../../../../SuperWarehouse/Components/SearchWarehouse/WarehouseSearch';
import './UserList.css';
import { getRemoteConfigSelector } from '../../../../../../redux/reducers/reducerSlices/RemoteConfigReducer';
import { getCountryList } from '../../../../../../redux/reducers/reducerSlices/GetCountryListReducer';
import { smartScheduleSelector } from '../../../../../../redux/reducers/reducerSlices/SmartScheduleReducer';

const UserList = (props) => {
    const { themeSelected } = props;
    const [showModal, setShowModal] = useState(false);
    const [createUser, setCreateUser] = useState({});
    const [userPopupHeading, setUserPopupHeading] = useState(AppConstant.user.popup.createUser.title)
    const dispatch = useDispatch();
    const user = useSelector(getUserSelector);
    const { permissions, closeCompanyForm, error, cu_id, userType, parentWarehouse, addCarrierSpro } = user;
    const [loading, setLoading] = useState(false);
    const [editData, setEditData] = useState({});
    const [resetFormData, setResetFormData] = useState(0);
    const [allowPermissionC, setAllowPermissionC] = useState(false)
    const [allowPermissionA, setAllowPermissionA] = useState(false)
    const [allowPermissionD, setAllowPermissionD] = useState(false)
    const [allowPermissionR, setAllowPermissionR] = useState(true)
    const [searchStr, setSearchStr] = useState('');

    // Carrier-related state (mirrors RecommendationSS.jsx pattern)
    const [carrierListState, setCarrierListState] = useState([]);
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [searchableSelectFormData, setSearchableSelectFormData] = useState(null);
    const [isEditingCarrierUser, setIsEditingCarrierUser] = useState(false);
    const [carrierSCAC, setCarrierSCAC] = useState('');
    const [isScacDisabled, setIsScacDisabled] = useState(false);
    const [particularFieldValue, setParticularFieldValue] = useState(null);
    // Holds the basePayload while waiting for a new carrier to be created via API
    const [pendingUserUpdatePayload, setPendingUserUpdatePayload] = useState(null);

    // Remote config
    const remoteConfig = useSelector(getRemoteConfigSelector);
    const { allConfigration } = remoteConfig;
    const countryData = useSelector(getCountryList);

    // Carrier list from SmartSchedule reducer (same source as RecommendationSS.jsx)
    const ssReducer = useSelector(smartScheduleSelector);
    const { carrierList } = ssReducer;

    useEffect(() => {
        if (permissions.length) {
            let tempC = shouldHavePermission(permissions, '501')
            setAllowPermissionC(tempC);
            let tempE = shouldHavePermission(permissions, '502')
            setAllowPermissionA(tempE);
            let tempD = shouldHavePermission(permissions, '503')
            setAllowPermissionD(tempD);
            dispatch({ type: sagaActions.GET_COUNTRY_DATA });
        }
    }, [])

    useEffect(() => {
        setLoading(false)
        if (closeCompanyForm) {
            closepopup();
            document.getElementById('close-btn')?.click();
            dispatch({ type: sagaActions.UPDATE_COMPANY_FORM_POPUP, payload: false });
        }
    }, [closeCompanyForm])

    useEffect(() => {
        if (error) {
            setLoading(false);
            dispatch({ type: sagaActions.UPDATE_USER_ERROR, payload: false });
        }
    }, [error])

    /* Refresh carrier dropdown options when Redux carrierList changes */
    useEffect(() => {
        formCarrierListData();
    }, [carrierList]);

    /**
     * When a new carrier is created via ADD_CARRIER_SCHEDULE_PRO, this effect fires.
     * Complete the pending user-update dispatch with the newly assigned carrier_id.
     */
    useEffect(() => {
        if (!_.isEmpty(addCarrierSpro) && pendingUserUpdatePayload) {
            // Format CarrierName as "CLEAN NAME (SCAC)" using the SCAC from the pending payload
            const scac = pendingUserUpdatePayload.SCAC || 'NANA';
            const cleanName = cleanCarrierName(addCarrierSpro.carrier_name || '');
            const finalPayload = {
                ...pendingUserUpdatePayload,
                IDCarrier: addCarrierSpro.carrier_id,
                CarrierName: `${cleanName} (${scac})`,
                SCAC: scac,
            };
            dispatch({ type: sagaActions.UPDATE_USER_LIST, payload: finalPayload });
            setPendingUserUpdatePayload(null);
            // Reset addCarrierSpro in Redux (type 'C' hits the else branch → null)
            dispatch({ type: sagaActions.ADD_CARRIER_SCHEDULE_PRO, payload: { type: 'C' } });
        }
    }, [addCarrierSpro]);

    // ─── Carrier Helpers ─────────────────────────────────────────────────────────

    /**
     * Extracts the SCAC code embedded in the trailing brackets of a carrier name.
     * e.g. "JB HUNT (HJBT)" → "HJBT"
     * Returns empty string if no brackets found.
     */
    const extractScacFromCarrierName = (carrierName = '') => {
        const match = carrierName.match(/\(([^)]+)\)$/);
        return match ? match[1].trim() : '';
    };

    /**
     * Strips the trailing SCAC bracket suffix from a carrier label so that
     * only the plain carrier name is stored in the DB.
     * e.g. "TEST TONE (TT01)" → "TEST TONE"
     * e.g. "JB HUNT (HJBT)"  → "JB HUNT"
     * Returns the original string unchanged if no brackets are found.
     */
    const cleanCarrierName = (carrierName = '') =>
        carrierName.replace(/\s*\([^)]+\)\s*$/, '').trim();

    /**
     * Returns true only when the SCAC is a real, usable code.
     * All N/A variants ("N/A", "NA", "NANA") and empty strings are treated
     * as "not real" → SCAC field remains editable.
     */
    const isRealScacCode = (scac = '') => {
        const upper = scac.trim().toUpperCase();
        return upper !== '' && upper !== 'N/A' && upper !== 'NA' && upper !== 'NANA';
    };

    // ─── Carrier Handlers (mirrors RecommendationSS.jsx pattern) ───────────────

    /** Format Redux carrierList into react-select options */
    const formCarrierListData = () => {
        if (_.size(carrierList)) {
            const carrierData = carrierList.map((el) => ({
                value: el.carrier_id,
                label: el.carrier_name,
            }));
            setCarrierListState([...carrierData]);
        } else {
            setCarrierListState([]);
        }
    };

    /** Triggered when user types in the carrier search input (≥3 chars fetches from API) */
    const onCarrierSelcted = (inputVal) => {
        dispatch({ type: sagaActions.SS_CLEAR_CARRIER_LIST });
        setCarrierListState([]);
        if (inputVal.length > 2) {
            dispatch({
                type: sagaActions.SS_GET_CARRIER_LIST,
                payload: { carrier: inputVal },
            });
        }
    };

    /**
     * Creates a new carrier option typed by user (not found in list).
     * Clears SCAC and makes it editable — mirrors RecommendationSS.jsx onCreateSelected.
     */
    const onCreateSelected = (carrierName) => {
        const newVal = { label: carrierName, value: null };
        setSelectedCarrier(newVal);
        setSearchableSelectFormData(newVal);
        setCarrierSCAC('');
        setIsScacDisabled(false);
        setParticularFieldValue({ carrier_scac: '', carrier_name: null });
    };

    // ─── Form Field Data Handler ────────────────────────────────────────────────

    /**
     * Called by CustomDynamicForm on any field change.
     * - carrier_name: look up SCAC from carrierList and auto-fill.
     * - carrier_scac: sync to carrierSCAC state.
     * Mirrors RecommendationSS.jsx getFormFieldData logic.
     */
    const getFormFieldData = (data, name, control, inputsObj, searchableData) => {
        if (name === 'carrier_name') {
            // searchableData is the full { label, value } react-select object.
            // data is just the raw carrier ID string (CustomDynamicForm passes data.value).
            // We must store the full object so selectedCarrier.label / .value work in the payload.
            setSelectedCarrier(searchableData ?? null);
            if (searchableData) {
                setSearchableSelectFormData(searchableData);
            }
            // Look up the selected carrier's SCAC from Redux carrierList.
            // Try multiple field name variants (SCAC, carrier_scac, scac).
            // Fall back to extracting from label brackets e.g. "JB HUNT (HJBT)" → "HJBT".
            const selectedCarrierObj = carrierList?.find(
                (el) => el.carrier_id === searchableData?.value
            );
            const scacFromObj = selectedCarrierObj?.SCAC
                || selectedCarrierObj?.carrier_scac
                || selectedCarrierObj?.scac
                || '';
            const scacFromLabel = extractScacFromCarrierName(searchableData?.label || '');
            const carrierScac = scacFromObj || scacFromLabel;

            setCarrierSCAC(carrierScac);
            // SCAC is always editable — no longer disabled for real/known codes
            setIsScacDisabled(false);
            setParticularFieldValue({
                carrier_scac: carrierScac,
                carrier_name: searchableData?.value,
            });
            dispatch({ type: sagaActions.SS_CLEAR_CARRIER_LIST });
            setCarrierListState([]);
        }
        if (name === 'carrier_scac') {
            setCarrierSCAC(data);
        }
    };

    // ─── Popup Helpers ──────────────────────────────────────────────────────────

    const showCreateUserPopup = () => {
        const popupObj = {
            id: 'createUserFormPopup',
            modalLabel: 'createUserFormPopup',
            showClose: true,
            showfooter: false,
            showHeader: true,
            sendDriverNotification: false,
            modalSize: 'md',
            className: "createUserPopup",
            title: userPopupHeading,
            bodyText: createForm(),
            subText: "",
            keyboard: true,
            backdrop: true,
            footerBtn: {
                btn1: {
                    show: true,
                    text: AppConstant.comments.cancelCommentButtonTitle
                },
                btn2: {
                    show: true,
                    text: AppConstant.login.save
                }
            },
        }

        return <Popup {...popupObj} popupBtnHandler={(text) => () => ''} closepopup={closepopup} themeSelected={themeSelected} showModal={showModal} />
    }

    const showEditUserPopup = () => {
        const popupObj = {
            id: 'editUserFormPopup',
            modalLabel: 'editUserFormPopup',
            showClose: true,
            showfooter: false,
            showHeader: true,
            sendDriverNotification: false,
            modalSize: 'md',
            className: "createUserPopup",
            title: AppConstant.user.popup.editUser.title,
            bodyText: createForm(),
            subText: "",
            keyboard: true,
            backdrop: true,
            footerBtn: {
                btn1: {
                    show: true,
                    text: AppConstant.comments.cancelCommentButtonTitle
                },
                btn2: {
                    show: true,
                    text: AppConstant.commonStrings.update
                }
            },
        }

        return <Popup {...popupObj} popupBtnHandler={(text) => () => ''} closepopup={closepopup} themeSelected={themeSelected} showModal={showModal} />
    }

    const cancelbtndata = {
        show: true,
        text: AppConstant.comments.resetbutton,
    };

    const submitbtnData = {
        show: true,
        text: AppConstant.superUserAdmin.warehouseAdmin.createWarehouseForm.saveButtonTitle,
    };

    /** Triggered when the Edit icon is clicked on a user row */
    const editClicked = (data, index) => {
        const tempData = updateUserPayload(data, false);
        setUserPopupHeading(AppConstant.user.popup.editUser.title);

        // Detect carrier user and pre-populate carrier dropdown + SCAC
        if (_.toUpper(data.UserType) === AppConstant.userPermission.usertypes.carrier) {
            setIsEditingCarrierUser(true);
            // SCAC: use data.SCAC (primary); fall back to extracting from CarrierName brackets
            // for backward compat with old records that stored "CARRIER NAME (SCAC)".
            const extractedScac = extractScacFromCarrierName(data.CarrierName || '');
            const scacVal = data.SCAC || extractedScac || '';
            // Build display label: clean carrier name + SCAC in brackets
            const cleanName = cleanCarrierName(data.CarrierName || '');
            const displayLabel = scacVal ? `${cleanName} (${scacVal})` : (data.CarrierName || '');
            const carrierVal = {
                label: displayLabel,
                value: data.IDCarrier || null,
            };
            setSelectedCarrier(carrierVal);
            setSearchableSelectFormData(carrierVal);
            setCarrierSCAC(scacVal);
            // SCAC is always editable — requirement: user can update SCAC at any time
            setIsScacDisabled(false);
            // Include carrier fields directly in editData — editFormData effect
            // populates them on mount. particularFieldValue stays null so the
            // stale-closure effect in CustomDynamicForm is NOT triggered on mount.
            setEditData({
                ...tempData,
                carrier_name: data.IDCarrier || null,
                carrier_scac: scacVal,
            });
            setParticularFieldValue(null);
        } else {
            setIsEditingCarrierUser(false);
            setSelectedCarrier(null);
            setSearchableSelectFormData(null);
            setCarrierSCAC('');
            setIsScacDisabled(false);
            setParticularFieldValue(null);
            setEditData({ ...tempData });
        }

        setShowModal(true);
    };

    const getSummitedFormData = (data) => {
        setLoading(true);
        dispatch({ type: sagaActions.UPDATE_COMPANY_FORM_POPUP, payload: false });
        if (data?.crud_type === 'U') {
            if (!_.isEmpty(data)) {
                const contactNum = data.phone_number
                    .replaceAll('(', '')
                    .replaceAll(')', '')
                    .replaceAll(' ', '')
                    .replaceAll('-', '');
                data.phone_number = contactNum;
                const basePayload = updateUserPayload(
                    data,
                    true,
                    cu_id,
                    userType === AppConstant.userPermission.usertypes.superAdmin ||
                    userType === AppConstant.userPermission.usertypes.administrator
                        ? null
                        : parentWarehouse.ID
                );
                // Always include IDCarrier, CarrierName and SCAC in the payload.
                // Carrier users: populate from state; non-carrier users: send null.
                // user_add_type must be false for carrier users — the backend routes
                // to a different stored procedure when it is true.
                if (isEditingCarrierUser) {
                    const scac = (data?.carrier_scac ?? carrierSCAC ?? '').trim() || 'NANA';
                    // Validate SCAC: must be exactly 4 characters (NANA is the 4-char fallback)
                    if (scac.length !== 4) {
                        showAlert(toast.TYPE.ERROR, 'SCAC code should be 4 characters only', themeSelected);
                        setLoading(false);
                        return;
                    }
                    if (selectedCarrier?.value === null || selectedCarrier?.value === undefined) {
                        // New carrier typed by user — create it first, then update user.
                        // Store the base payload; the useEffect([addCarrierSpro]) will
                        // complete the dispatch once the carrier_id is returned.
                        // CarrierName stored as "CLEAN NAME (SCAC)" format.
                        const cleanName = cleanCarrierName(selectedCarrier?.label ?? '');
                        const pendingPayload = {
                            ...basePayload,
                            user_add_type: false,
                            IDCarrier: null,          // filled in by useEffect
                            CarrierName: `${cleanName} (${scac})`,
                            SCAC: scac,
                        };
                        setPendingUserUpdatePayload(pendingPayload);
                        dispatch({
                            type: sagaActions.ADD_CARRIER_SCHEDULE_PRO,
                            payload: {
                                name: selectedCarrier?.label,
                                SCAC: scac !== 'NANA' ? scac : '',
                                contact_phone: null,
                                first_name: null,
                                last_name: null,
                                type: 'A',
                            },
                        });
                    } else {
                        // Existing carrier selected — dispatch user update directly.
                        // Strip old SCAC from label (if any) then append the current one.
                        const cleanName = cleanCarrierName(selectedCarrier?.label ?? '');
                        const finalPayload = {
                            ...basePayload,
                            user_add_type: false,
                            IDCarrier: selectedCarrier?.value ?? null,
                            CarrierName: `${cleanName} (${scac})`,
                            SCAC: scac,
                        };
                        dispatch({ type: sagaActions.UPDATE_USER_LIST, payload: finalPayload });
                    }
                } else {
                    const finalPayload = {
                        ...basePayload,
                        IDCarrier: null,
                        CarrierName: null,
                        SCAC: null,
                    };
                    dispatch({ type: sagaActions.UPDATE_USER_LIST, payload: finalPayload });
                }
            } else {
                showAlert(toast.TYPE.WARNING, AppConstant.user.toast.blankForm, themeSelected);
            }
        } else {
            setCreateUser({ ...data });
            if (!_.isEmpty(data)) {
                let tempData = { ...data };
                tempData.user_name = data.email;
                tempData.p_cu_id =
                    userType === AppConstant.userPermission.usertypes.superAdmin ||
                    userType === AppConstant.userPermission.usertypes.administrator
                        ? null
                        : cu_id;
                tempData.warehouseId =
                    userType === AppConstant.userPermission.usertypes.superAdmin ||
                    userType === AppConstant.userPermission.usertypes.administrator
                        ? null
                        : parentWarehouse.ID;
                const contactNum = tempData.phone_number
                    .replaceAll('(', '')
                    .replaceAll(')', '')
                    .replaceAll(' ', '')
                    .replaceAll('-', '')
                    .replaceAll('+', '');
                tempData.phone_number = '+' + contactNum;
                dispatch({ type: sagaActions.REGISTER_USER, payload: tempData });
            } else {
                showAlert(toast.TYPE.WARNING, AppConstant.user.toast.blankForm, themeSelected);
            }
        }
    };

    const getUserTypeList = () => {
        // Build options from config, TRIMMING each value. formDropDownData() does not
        // trim, so entries after the first comma get a leading space (e.g. " USER"),
        // causing the <select value="USER"> to never match — field shows blank.
        const configValues = (allConfigration?.IMSUserType || '')
            .slice(1, -1)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .sort();

        const baseList = configValues.map((ele, index) => (
            <option key={index} value={ele}>{ele}</option>
        ));

        // In edit mode, guarantee the current user_type has an exact-match option.
        // Handles cases where the API value casing differs from the config string.
        if (userPopupHeading === AppConstant.user.popup.editUser.title && editData?.user_type) {
            const currentType = editData.user_type.trim();
            if (!configValues.includes(currentType)) {
                return [
                    ...baseList,
                    <option key="current-type-extra" value={currentType}>{currentType}</option>,
                ];
            }
        }
        return baseList;
    };

    /** Build form field list; conditionally adds carrier+SCAC fields for CARRIER users */
    const formFieldList = [
        {
            name: "email",
            type: "text",
            size: "col-md-12",
            position: 1,
            title: AppConstant.user.popup.createUser.fields.user_name,
            formValidation: {
                required: true,
                validateEmail: true,
            },
        },
        {
            name: "given_name",
            type: "text",
            size: "col-md-12",
            position: 2,
            title: AppConstant.user.popup.createUser.fields.given_name_title,
            formValidation: {
                required: true,
            },
        },
        {
            name: "family_name",
            type: "text",
            size: "col-md-12",
            position: 3,
            title: AppConstant.user.popup.createUser.fields.family_name_title,
            formValidation: {
                required: true,
            },
        },
        {
            name: "phone_number",
            type: "phone_with_country_code",
            size: "col-md-12",
            position: 4,
            title: AppConstant.user.popup.createUser.fields.phone_number_title,
            formValidation: {
                required: true,
            },
            placeholder: AppConstant.user.popup.createUser.phone_placeHolder,
            activeCountriesListCodes: getActiveCountryCode(countryData?.data),
        },
        {
            name: "user_type",
            type: "select",
            size: "col-sm-12",
            position: 5,
            title: AppConstant.commonStrings.userType,
            formValidation: {
                required: true,
            },
            disabledField: userPopupHeading === AppConstant.user.popup.editUser.title ? true : false,
            isEditDisable: true,
            getSelectData: getUserTypeList,
        },
        // Carrier dropdown + SCAC — only shown when editing a CARRIER-type user
        ...(isEditingCarrierUser ? [
            {
                name: "carrier_name",
                type: "select_with_input_search_add_button_temp",
                size: "col-sm-12 mb-1 f-12",
                parentClass: "inputTitle",
                componentClassName: "mt-1",
                inputTitle: "Select Carrier",
                position: 6,
                title: "Carrier Name",
                placeholderText: "Select a carrier from the list",
                onInputChange: onCarrierSelcted,
                isLoading: false,
                isClearable: true,
                isSearchable: true,
                options: carrierListState,
                formValidation: { required: false },
                searchableSelectFormData: searchableSelectFormData,
                handleCreateOption: onCreateSelected,
                defaultValue: !_.isEmpty(selectedCarrier)
                    ? selectedCarrier
                    : "Select a carrier from the list",
            },
            {
                name: "carrier_scac",
                type: "text",
                size: "col-sm-12 mb-1 f-12",
                position: 7,
                title: "SCAC Code (Optional)",
                formValidation: {
                    required: false,
                },
                placeholder: "Enter SCAC Code",
                disabledField: false,
            },
        ] : []),
    ];

    const closepopup = () => {
        setShowModal(false);
        setEditData({});
        setResetFormData(resetFormData + 1);
        setUserPopupHeading(AppConstant.user.popup.createUser.title);
        // Reset carrier state
        setIsEditingCarrierUser(false);
        setSelectedCarrier(null);
        setSearchableSelectFormData(null);
        setCarrierSCAC('');
        setIsScacDisabled(false);
        setParticularFieldValue(null);
        setPendingUserUpdatePayload(null);
        dispatch({ type: sagaActions.SS_CLEAR_CARRIER_LIST });
        setCarrierListState([]);
        // Reset any leftover addCarrierSpro state in Redux
        if (!_.isEmpty(addCarrierSpro)) {
            dispatch({ type: sagaActions.ADD_CARRIER_SCHEDULE_PRO, payload: { type: 'C' } });
        }
    };

    const createForm = () => {
        return (
            <CustomDynamicForm
                formFieldList={formFieldList}
                sort={true}
                cancelBtn={cancelbtndata}
                submitbtn={submitbtnData}
                themeSelected={themeSelected}
                getSummitedFormData={getSummitedFormData}
                getFormFieldData={getFormFieldData}
                editFormData={editData}
                loading={loading}
                resetFormData={resetFormData}
                setParticularFieldValue={particularFieldValue && Object.keys(particularFieldValue).length > 0 ? particularFieldValue : null}
                disclaimer={isEditingCarrierUser}
                disclaimersArray={
                    isEditingCarrierUser
                        ? [{ text: 'If carrier is changed the existing appointments won\'t show' }]
                        : []
                }
            />
        );
    };

    return (<div className='mt-2'>
        <div className='container-fluid d-flex py-2 text-start justify-content-between'>
            <CustomButton
                dataBsToggle="modal"
                dataBsTarget={`#createUserFormPopup`}
                onClick={() => {
                    setCreateUser({})
                    setUserPopupHeading(AppConstant.user.popup.createUser.title)
                    setShowModal(true);
                }}
                className={allowPermissionC ? 'addButton' : 'addButton disabled'}
                disabled={allowPermissionC ? '' : 'disabled'}
                title={AppConstant.user.createBtnTitle}
            />
            <div className='w-25'>
                <WarehouseSearch
                    placeholder={'Search By Name/Email/Carrier'}
                    inputValue={searchStr}
                    cancelSearch={() => setSearchStr('')}
                    onChangeHandler={(text) => setSearchStr(text)} />
            </div>
        </div>

        <div className="userListTable mt-1">
            <UserTable searchStr={searchStr} {...props} setCreateUser={setCreateUser} allowPermissionA={allowPermissionA} allowPermissionD={allowPermissionD} allowPermissionR={allowPermissionR} editUserAction={(userObj, index) => editClicked(userObj, index)} />
            {showCreateUserPopup()}
            {/* {showEditUserPopup()} */}
        </div>
    </div>);
}

export default UserList;